// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Filter, Search, UnfoldHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { ChannelsColumnSelector } from "@/components/channels-column-selector";
import { ColumnAliasModal } from "@/components/column-alias-modal";
import { CSVExportButton } from "@/components/csv-export-button";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import ResetTableMenu from "@/components/reset-table-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type ChannelMetrics,
  useChannelMetrics,
} from "@/hooks/api/use-channel-metrics";
import {
  type Channel,
  type ChannelsQueryParams,
  useChannelsExport,
  useChannelsWithMetrics,
} from "@/hooks/api/use-channels";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
import { useChannelFilterState } from "@/hooks/use-channel-filter-state";
import {
  useChannelListFilters,
  useChannelMetrics as useChannelMetricsConfig,
} from "@/hooks/use-channel-list-filters";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSearchState } from "@/hooks/use-search-state";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import type { ColumnAliases } from "@/lib/column-aliases";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDateForFilename } from "@/lib/date-formatting";
import type { MetricsFilters } from "@/lib/filters";
import { cn } from "@/lib/utils";
import {
  DRILLDOWN_LIMIT,
  METRIC_CONFIG,
} from "@/pages/channels/channel-explorer/channel-metrics-config";
import { MetricDrilldown } from "@/pages/channels/channel-explorer/metric-drilldown";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { getChannelColumnDefs, getChannelColumns } from "./channel-columns";

export function ChannelsDataTable() {
  const CHANNEL_METRIC_LIMIT = 5000;

  // Use the sidebar hook to get state
  const { state, isMobile } = useSidebar();

  // State for sorting and searching - Replace useState search with sessionStorage
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "name",
    direction: "asc",
  });

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("channels::list::search");

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedDrilldown, setSelectedDrilldown] = useState<{
    channelName: string;
    metric: keyof ChannelMetrics;
    metricValue?: number | string;
  } | null>(null);

  // Structured filter groups (account / opportunity / touchpoint)
  const channelFilterState = useChannelFilterState("channels::list");
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
    clearAllFilters: clearAllMetricFilters,
  } = channelFilterState;

  // Total count of active metric filters
  const totalMetricFilterCount = useMemo(
    () =>
      accountFilters.filters.length +
      opportunityFilters.filters.length +
      touchpointFilters.filters.length +
      campaignFilters.filters.length,
    [accountFilters, opportunityFilters, touchpointFilters, campaignFilters],
  );

  // Process metric filters for display using the same logic as Report Cards
  const {
    displayFilters: metricDisplayFilters,
    filterColumns: metricFilterColumns,
    handleClearFilter: handleClearMetricFilter,
  } = useMetricCardFilters(channelFilterState);

  // Use channel list filters hook
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  } = useChannelListFilters();

  // Use the channel metrics configuration hook
  const { selectedMetrics, setSelectedMetrics, getDefaultMetrics } =
    useChannelMetricsConfig();

  // Column aliases state management using sessionStorage (no longer persisted across sessions)
  const [columnAliases, setColumnAliases] =
    useOrganizationSessionStorage<ColumnAliases>(
      "channels::list::columns::aliases",
      {},
      {
        // Migrate from localStorage to sessionStorage
        migration: {
          from: {
            key: "channels::list::columns::aliases",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true, // Remove from localStorage after migration
        },
      },
    );
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [aliasColumnId, setAliasColumnId] = useState<string>("");

  // State to track if performance message should show
  const [showPerformanceMessage, setShowPerformanceMessage] = useState(false);

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings]);

  const handleSelectMetrics = useCallback(
    (metrics: Array<keyof ChannelMetrics>) => {
      // Check if any removed metrics are currently used for sorting
      const removedMetrics = selectedMetrics.filter(
        (metric) => !metrics.includes(metric),
      );

      // Check if the current sort is on a metric that's being removed
      if (sortConfig.key.startsWith("metric_")) {
        const currentMetricKey = sortConfig.key.replace(
          "metric_",
          "",
        ) as keyof ChannelMetrics;

        if (removedMetrics.includes(currentMetricKey)) {
          // Find the metric title from the config
          const metricConfig = METRIC_CONFIG[currentMetricKey];
          const metricTitle = metricConfig?.title || currentMetricKey;

          // Show toast warning
          toast.warning("Table sort reset", {
            description: `Sort on ${metricTitle} has been removed because the column was deselected.`,
            duration: 8000,
          });

          // Reset sort to defaults
          resetSortToDefaults();
          clearFilter("sortColumn");
          clearFilter("sortDirection");
        }
      }

      setSelectedMetrics(metrics);
    },
    [
      selectedMetrics,
      sortConfig.key,
      setSelectedMetrics,
      resetSortToDefaults,
      clearFilter,
    ],
  );

  // Handle metric click to open drill-down
  const handleMetricClick = useCallback(
    (
      channelName: string,
      metric: keyof ChannelMetrics,
      metricValue?: number | string,
    ) => {
      setSelectedDrilldown({ channelName, metric, metricValue });
    },
    [],
  );

  // Define columns for the table, including metric columns
  const columns = useMemo<UnifiedTableColumn<Channel>[]>(
    () => getChannelColumns(selectedMetrics, handleMetricClick),
    [selectedMetrics, handleMetricClick],
  );

  // Column visibility state for channels - using sessionStorage for tab-specific behavior
  const [columnVisibility, setColumnVisibility] = useOrganizationSessionStorage<
    Record<string, boolean>
  >(
    "channels::list::column-visibility",
    // By default, show all core columns
    columns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  // Reset column visibility **and** metric selection to defaults
  const resetColumnVisibility = useCallback(() => {
    // 1. Reset selected metrics to the default set
    const defaultMetrics = getDefaultMetrics();
    setSelectedMetrics(defaultMetrics);

    // 2. Build a fresh column list using those default metrics
    const defaultColumns = getChannelColumns(defaultMetrics, handleMetricClick);

    // 3. Show all of those columns by default
    const defaultVisibility = defaultColumns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    setColumnVisibility(defaultVisibility);
  }, [
    getDefaultMetrics,
    setSelectedMetrics,
    handleMetricClick,
    setColumnVisibility,
  ]);

  // Handle clear all filters and reset sort - Updated for sessionStorage
  const handleClearAllFilters = useCallback(() => {
    // Check if the current sort is a metric sort before clearing
    const currentSortKey = sortConfig.key;
    if (currentSortKey.startsWith("metric_")) {
      // Mark that a metric sort was cleared
      metricSortClearedRef.current = currentSortKey;
    }

    // Clear all filters
    clearAllFilters();

    // Clear search state from sessionStorage
    clearSearch();

    // Get default sort (name) and update UI state
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection as SortDirection,
    });

    // After clearing all filters, add ONLY the default sort back to filters
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [
    clearAllFilters,
    getDefaultSortSettings,
    updateFilter,
    sortConfig.key,
    clearSearch,
  ]);

  // Track metric sort cleared
  const metricSortClearedRef = useRef<string | null>(null);

  // When component mounts, set sort from filters (search is now in sessionStorage)
  useEffect(() => {
    // Set sort configuration from filters or use defaults
    if (filters.sortColumn) {
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as string as SortDirection,
      });
    } else {
      // No sort in filters, use defaults
      const defaults = getDefaultSortSettings();
      setSortConfig({
        key: defaults.sortColumn,
        direction: defaults.sortDirection as SortDirection,
      });

      // Add sort to filters
      updateFilter("sortColumn", defaults.sortColumn);
      updateFilter("sortDirection", defaults.sortDirection);
    }
  }, [
    filters.sortColumn,
    filters.sortDirection,
    getDefaultSortSettings,
    updateFilter,
  ]);

  // Build query parameters for single API call with embedded metrics
  const queryParams = useMemo<ChannelsQueryParams>(() => {
    const metricsFilters = {
      ...(hasAccountFilters && { account_filters: accountFilters }),
      ...(hasOpportunityFilters && { opportunity_filters: opportunityFilters }),
      ...(hasTouchpointFilters && { touchpoint_filters: touchpointFilters }),
      ...(hasCampaignFilters && { campaign_filters: campaignFilters }),
    };

    return getQueryParams({
      searchTerm: debouncedSearchQuery,
      columns: columns,
      limit: 100, // Channels are typically a small set (10-50 total)
      metrics: selectedMetrics.length > 0 ? selectedMetrics : undefined,
      metric_filters:
        Object.keys(metricsFilters).length > 0 ? metricsFilters : undefined,
    });
  }, [
    debouncedSearchQuery,
    selectedMetrics,
    hasAccountFilters,
    accountFilters,
    hasOpportunityFilters,
    opportunityFilters,
    hasTouchpointFilters,
    touchpointFilters,
    hasCampaignFilters,
    campaignFilters,
    getQueryParams,
    columns,
  ]);

  // Single API call with embedded metrics (like Campaign List)
  const {
    data: channelsData,
    isLoading: isLoadingChannels,
    isError,
    error,
  } = useChannelsWithMetrics(queryParams);

  // Channels with metrics are already included in the response
  const channels = useMemo(() => {
    const channelsWithMetrics = channelsData?.results || [];

    // Apply client-side sorting if needed
    if (sortConfig.key === "name") {
      return [...channelsWithMetrics].sort((a, b) => {
        const comparison = a.name.localeCompare(b.name);
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    // Handle metric sorting
    if (sortConfig.key.startsWith("metric_")) {
      const metricKey = sortConfig.key.replace(
        "metric_",
        "",
      ) as keyof ChannelMetrics;
      return [...channelsWithMetrics].sort((a, b) => {
        const aValue =
          typeof a.metrics?.[metricKey] === "number" ? a.metrics[metricKey] : 0;
        const bValue =
          typeof b.metrics?.[metricKey] === "number" ? b.metrics[metricKey] : 0;
        const comparison = aValue - bValue;
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    return channelsWithMetrics;
  }, [channelsData, sortConfig]);

  // Setup for CSV export
  const { exportChannelsAsync } = useChannelsExport();

  // Set up CSV export with proper parameters
  const { exportData, isExporting, defaultFilename } = usePaginatedCSVExport({
    fetchAllData: () => exportChannelsAsync(queryParams),
    columns,
    filename: `channels-${formatDateForFilename(new Date())}.csv`,
    onError: (error) => {
      console.error("CSV export failed:", error);
      toast.error("Failed to export channels data");
    },
  });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Search is now managed by useSearchState hook - no need for manual debouncing

  // Column state management (with resizing)
  const {
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    resetToDefaultPinned,
    dndState: { sensors, handleDragEnd },
    getInitializeColumnOrder,
  } = usePersistedColumnState("channels::list::columns", {
    getDefaultPinnedColumns: () => ["name"],
  });

  // Convert to TanStack columnDefs with resizing enabled
  const columnDefs = useMemo<ColumnDef<Channel>[]>(() => {
    const allDefs = getChannelColumnDefs(selectedMetrics, handleMetricClick);
    return allDefs.map((col) => ({ ...col, enableResizing: true }));
  }, [selectedMetrics, handleMetricClick]);

  // Function to handle double-click on column resize handle to auto-size
  const handleDoubleClickResize = useCallback(
    createColumnResizeHandler(setColumnSizing, "channels-table"),
    [],
  );

  // Single column resize handler for kebab menu
  const handleSingleColumnResize = useMemo(
    () => createSingleColumnResizeHandler(setColumnSizing, "channels-table"),
    [setColumnSizing],
  );

  // Bulk resize handler for autosize all button
  const handleBulkColumnResize = useMemo(
    () => createBulkColumnResizeHandler(setColumnSizing, "channels-table"),
    [setColumnSizing],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      // Find the column to check its type
      const column = columns.find((col) => col.key === columnId);

      // For metric columns, remove from selectedMetrics
      if (column?.columnType === "metric") {
        // Extract metric key from column ID following our naming convention
        // Column IDs are prefixed for uniqueness: "metric_accounts_engaged"
        // But selectedMetrics state and API expect unprefixed keys: "accounts_engaged"
        const metricKey = columnId.replace(
          "metric_",
          "",
        ) as keyof ChannelMetrics;
        setSelectedMetrics((prev) => prev.filter((m) => m !== metricKey));
      } else {
        // For all other columns (core, custom), update visibility
        setColumnVisibility((prev) => ({
          ...prev,
          [columnId]: false,
        }));
      }
    },
    [columns, setColumnVisibility, setSelectedMetrics],
  );

  // Column alias handlers
  const handleSetAlias = useCallback((columnId: string) => {
    setAliasColumnId(columnId);
    setShowAliasModal(true);
  }, []);

  const handleSaveAlias = useCallback(
    (columnId: string, alias: string) => {
      setColumnAliases((prev) => ({ ...prev, [columnId]: alias }));
    },
    [setColumnAliases],
  );

  const handleClearAlias = useCallback(
    (columnId: string) => {
      setColumnAliases((prev) => {
        const updated = { ...prev };
        delete updated[columnId];
        return updated;
      });
    },
    [setColumnAliases],
  );

  const handleResetAliases = useCallback(() => {
    setColumnAliases({});
  }, [setColumnAliases]);

  // Reference to container element
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // Initialize column order when columnDefs change
  useEffect(() => {
    const initializeOrder = getInitializeColumnOrder(columnDefs);
    initializeOrder();
  }, [columnDefs, getInitializeColumnOrder]);

  // Table configuration
  const table = useReactTable({
    data: channels,
    columns: columnDefs,
    state: {
      columnSizing,
      columnOrder,
      columnVisibility,
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    defaultColumn: {
      minSize: 80,
      size: 150,
    },
  });

  // Total count of channels
  const totalCount = channels.length;

  // Loading state - only block UI for channels loading
  const isLoading = isLoadingChannels;

  // Show performance message after 5 seconds of loading
  useEffect(() => {
    if (isLoading && selectedMetrics.length > 0) {
      const timer = setTimeout(() => {
        setShowPerformanceMessage(true);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    } else {
      setShowPerformanceMessage(false);
    }
  }, [isLoading, selectedMetrics.length]);

  // Check if the metric limit is exceeded
  const isMetricLimitExceeded = totalCount > CHANNEL_METRIC_LIMIT;

  // Handle sorting
  const handleSort = (colId: string) => {
    const unifiedColumn = columns.find((col) => col.key.toString() === colId);
    const sortable = unifiedColumn?.sortable;

    if (!sortable) return;

    // Skip sorting on metric columns when metric limit is exceeded
    const isMetricColumn = colId.startsWith("metric_");
    if (isMetricColumn && isMetricLimitExceeded) return;

    let direction: SortDirection = unifiedColumn?.defaultSortDirection || "asc";

    if (sortConfig.key === colId) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    // Update UI state
    setSortConfig({ key: colId, direction });

    // Update filters
    updateFilter("sortColumn", colId);
    updateFilter("sortDirection", direction);
  };

  // Search functions are now provided by useSearchState hook

  // Render sort icon
  const renderSortIcon = useCallback(
    (props: { key: string; sortable?: boolean }) => {
      if (!props.sortable) return null;

      const isMetricColumn = props.key.startsWith("metric_");
      if (isMetricColumn && isMetricLimitExceeded) {
        return null;
      }

      return (
        <SortIcon
          isActive={sortConfig.key === props.key}
          direction={sortConfig.key === props.key ? sortConfig.direction : null}
          showOnHover={false}
        />
      );
    },
    [sortConfig, isMetricLimitExceeded],
  );

  // Helper function for metric limit tooltip
  const getMetricLimitTooltipText = () => {
    return `Apply additional filters to sort on a metric. For performance reasons, sorting on metrics is currently disabled when more than ${CHANNEL_METRIC_LIMIT} channels match filters.`;
  };

  // Memoize pinnedColumnPositions calculation at component level to avoid React hooks rules violation
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render loading state
  const renderLoadingState = () => {
    const headerGroups = table.getHeaderGroups();

    return (
      <>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  <Skeleton className="h-4 w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-card">
          {Array.from({ length: 7 }).map((_, i) => (
            <TableRow key={i}>
              {i === 3 && showPerformanceMessage ? (
                <TableCell
                  colSpan={headerGroups[0].headers.length}
                  className="py-6"
                >
                  <div className="flex items-center">
                    <div
                      role="status"
                      aria-live="polite"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-muted/50 border border-muted-foreground/10 animate-in fade-in duration-500"
                    >
                      <svg
                        className="w-4 h-4 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-muted-foreground">
                        To speed up query time, try adding metric filters or
                        reducing the number of metric columns
                      </span>
                    </div>
                  </div>
                </TableCell>
              ) : (
                headerGroups[0].headers.map((header) => (
                  <TableCell key={`${i}-${header.id}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Generate export button tooltip
  const exportTooltipText = useMemo(() => {
    if (isMetricLimitExceeded && selectedMetrics.length > 0) {
      return `To maintain performance, exporting metrics for more than ${CHANNEL_METRIC_LIMIT} channels is not currently supported. Please filter to less than ${CHANNEL_METRIC_LIMIT} channels, or remove metric columns in order to export`;
    }
    return "Export channels data to CSV";
  }, [isMetricLimitExceeded, selectedMetrics.length]);

  // Determine if export should be disabled
  const isExportDisabled = useMemo(() => {
    return (
      isExporting ||
      isLoading ||
      channels.length === 0 ||
      (isMetricLimitExceeded && selectedMetrics.length > 0)
    );
  }, [
    isExporting,
    isLoading,
    channels.length,
    isMetricLimitExceeded,
    selectedMetrics.length,
  ]);

  // Reset ONLY column widths
  const resetColumnWidths = useCallback(() => {
    setColumnSizing({});
  }, [setColumnSizing]);

  // Reset ONLY column order (preserve pinned columns at the start)
  const resetColumnOrder = useCallback(() => {
    setColumnOrder((prev) => {
      const defaultOrder = columnDefs.map((c) => c.id!);
      const pinnedFirst = prev.filter((id) => pinnedColumns.includes(id));
      const unpinnedDefault = defaultOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );
      return [...pinnedFirst, ...unpinnedDefault];
    });
  }, [setColumnOrder, columnDefs, pinnedColumns]);

  // Reset pinned columns
  const resetPinnedColumns = useCallback(() => {
    resetToDefaultPinned();
  }, [resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order to defaults
  const resetSortOrder = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Comprehensive table reset function
  const handleResetTable = useCallback(() => {
    // Clear all filters and search
    handleClearAllFilters();

    // Reset column visibility to default
    resetColumnVisibility();

    // Reset metric filters
    clearAllMetricFilters();

    // Reset column order then widths
    setColumnOrder([]);
    setColumnSizing({});

    // Reset pinned columns
    resetPinnedColumns();

    // Reset column aliases
    setColumnAliases({});

    // Reset wrapped columns
    resetWrappedColumns();

    // Trigger column auto-adjust
    // Table state clearing is handled by organization-scoped storage hooks

    // Show success toast
    toast.success("Table reset to default state");
  }, [
    handleClearAllFilters,
    resetColumnVisibility,
    clearAllMetricFilters,
    setColumnOrder,
    setColumnSizing,
    resetPinnedColumns,
    setColumnAliases,
    resetWrappedColumns,
  ]);

  // Render table content (headers, body, etc.) without placing DndContext inside thead
  const renderTableContent = () => {
    const rowModel = table.getRowModel();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          handleDoubleClickResize={handleDoubleClickResize}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          onRemoveColumn={handleRemoveColumn}
          columnAliases={columnAliases}
          onSetAlias={handleSetAlias}
        />
        <TableBody className="bg-card">
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading channels: {error.message}
              </TableCell>
            </TableRow>
          ) : channelsData?.errors?.some((e) => e.code === "QUERY_TIMEOUT") ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                The metrics query timed out. To speed up query time, try adding
                metric filters or reducing the number of metric columns.
              </TableCell>
            </TableRow>
          ) : channels.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                No matching channels
              </TableCell>
            </TableRow>
          ) : (
            rowModel.rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const isPinned = pinnedColumns.includes(cell.column.id);
                  const isWrapped = wrappedColumns.includes(cell.column.id);
                  const pinnedLeft = pinnedColumnPositions[cell.column.id];

                  return (
                    <TableCell
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                        maxWidth: cell.column.getSize(),
                        ...(isPinned && { left: `${pinnedLeft}px` }),
                      }}
                      className={cn(
                        cell.column.id === sortConfig.key && "bg-muted/40",
                        isPinned && "pinned-column bg-background z-50",
                      )}
                      data-column-id={cell.column.id}
                    >
                      <div
                        className={cn("cell-content", isWrapped && "wrapped")}
                        title={String(row.getValue(cell.column.id) || "")}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </>
    );
  };

  return (
    <Card className="h-full relative flex flex-col">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by channel name..."
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              onChange={(e) => handleSearchChange(e.target.value)}
              value={searchInputValue}
              aria-label="Search channels"
            />
            {searchInputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
          <InfoTooltip
            trigger={
              <button
                className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Wildcard search info"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoCircledIcon className="h-3.5 w-3.5" />
              </button>
            }
            side="right"
            sideOffset={5}
          >
            <div className="text-sm space-y-2">
              <p className="font-medium">Supports wildcards</p>
              <ul className="space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    _
                  </code>{" "}
                  matches a single character
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    %
                  </code>{" "}
                  matches zero or more characters
                </li>
              </ul>
            </div>
          </InfoTooltip>
        </div>
        <div className="flex items-center gap-2">
          {/* Record Count - First item in right-side button group */}
          <div className="text-sm text-muted-foreground">
            {isLoading
              ? "Loading..."
              : totalCount > 0
                ? `${totalCount.toLocaleString()} matching channels`
                : "No matching channels"}
          </div>
          {/* Channels Column & Metrics Selector */}
          <ChannelsColumnSelector
            selectedMetrics={selectedMetrics}
            onSelectMetrics={handleSelectMetrics}
            showMetricsSection={true}
            defaultMetrics={getDefaultMetrics()}
          />

          {/* Metric Filters Button - Only show when metrics are selected */}
          {selectedMetrics.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
            >
              <Filter
                className={`h-4 w-4 ${
                  hasAccountFilters ||
                  hasOpportunityFilters ||
                  hasTouchpointFilters
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Metric Filters</span>
              {totalMetricFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {totalMetricFilterCount}
                </Badge>
              )}
            </Button>
          )}

          {/* Autosize All Columns Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  const visibleColumnIds = columnOrder.filter(
                    (columnId) => columnVisibility[columnId] !== false,
                  );
                  handleBulkColumnResize(visibleColumnIds);
                }}
                className="h-8 w-8"
              >
                <UnfoldHorizontal className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resize all visible columns to fit their content</p>
            </TooltipContent>
          </Tooltip>

          {/* Reset Table Menu */}
          <ResetTableMenu
            onResetWidths={resetColumnWidths}
            onResetOrder={resetColumnOrder}
            onResetColumns={resetColumnVisibility}
            onResetTableFilters={handleClearAllFilters}
            onResetMetricFilters={clearAllMetricFilters}
            onResetPinnedColumns={resetPinnedColumns}
            onResetSort={resetSortOrder}
            onResetAliases={handleResetAliases}
            onResetWrappedColumns={resetWrappedColumns}
            onResetAll={handleResetTable}
          />

          {/* CSV Export Button */}
          <ExportFilenameDialog
            open={isExportDialogOpen}
            onOpenChange={setIsExportDialogOpen}
            defaultFilename={defaultFilename}
            extension="csv"
            onExport={handleExport}
            disabled={isExportDisabled}
          >
            <CSVExportButton
              onClick={() => setIsExportDialogOpen(true)}
              isExporting={isExporting}
              disabled={isExportDisabled}
              tooltipText={exportTooltipText}
            />
          </ExportFilenameDialog>
        </div>
      </div>

      {/* Filter Modal */}
      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="channels::list"
        entity="channel"
      />

      {/* Active Filters Display - Show both table-level and metric filters */}
      {/* Active Filters Display – reserve 32px */}
      <div className="px-4 pb-2 space-y-2 min-h-8 flex-shrink-0">
        {/* Table-level filters */}
        {hasFilters && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={
                // Filter out sort-related properties from display
                Object.fromEntries(
                  Object.entries(filters).filter(
                    ([key]) => key !== "sortColumn" && key !== "sortDirection",
                  ),
                )
              }
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all table filters"
              filterType="table"
            />
          </div>
        )}

        {/* Metric filters */}
        {Object.keys(metricDisplayFilters).length > 0 && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={metricDisplayFilters}
              columns={metricFilterColumns}
              clearFilter={handleClearMetricFilter}
              clearAllFilters={clearAllMetricFilters}
              clearAllText="Clear all metric filters"
              filterType="metric"
            />
          </div>
        )}
      </div>

      {/* Main Table Container */}
      <DndContext
        sensors={sensors}
        collisionDetection={constrainedClosestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToTableBounds]}
      >
        <div
          ref={tableContainerRef}
          className={cn(
            "rounded-md border-t border-x overflow-y-auto flex-grow",
            table.getState().columnSizingInfo.isResizingColumn &&
              "table-resizing",
          )}
          style={{
            maxWidth: isMobile
              ? "calc(100vw - 2rem)"
              : state === "collapsed"
                ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                : "calc(100vw - var(--sidebar-width) - 2rem)",
            minWidth: "640px",
          }}
        >
          <Table
            className="w-full table-fixed"
            style={{ width: table.getTotalSize() }}
            role="table"
            aria-label="Channels data table"
            aria-describedby="channel-list-heading"
            data-table-id="channels-table"
            data-table-container
          >
            {isLoading ? renderLoadingState() : renderTableContent()}
          </Table>
        </div>
      </DndContext>

      {/* Metric Drilldown Modal */}
      {selectedDrilldown && (
        <MetricDrilldownWrapper
          channelName={selectedDrilldown.channelName}
          metric={selectedDrilldown.metric}
          metricValue={selectedDrilldown.metricValue}
          onClose={() => setSelectedDrilldown(null)}
          filters={{
            ...(hasAccountFilters && {
              account_filters: accountFilters,
            }),
            ...(hasOpportunityFilters && {
              opportunity_filters: opportunityFilters,
            }),
            ...(hasTouchpointFilters && {
              touchpoint_filters: touchpointFilters,
            }),
            ...(hasCampaignFilters && {
              campaign_filters: campaignFilters,
            }),
          }}
        />
      )}

      {/* Column Alias Modal */}
      <ColumnAliasModal
        open={showAliasModal}
        onOpenChange={setShowAliasModal}
        columnId={aliasColumnId}
        originalTitle={(() => {
          const column = columns.find((col) => col.key === aliasColumnId);
          return typeof column?.title === "string"
            ? column.title
            : aliasColumnId;
        })()}
        originalDescription={(() => {
          if (aliasColumnId.startsWith("metric_")) {
            const metricKey = aliasColumnId.replace("metric_", "");
            return METRIC_CONFIG[metricKey as keyof typeof METRIC_CONFIG]
              ?.tooltip;
          }
          return undefined;
        })()}
        currentAlias={columnAliases[aliasColumnId]}
        onSave={handleSaveAlias}
        onClear={handleClearAlias}
      />
    </Card>
  );
}

// Mapping from derived metrics to their base metrics
const METRIC_TO_BASE: Record<string, keyof ChannelMetrics> = {
  // Amount metrics map to their count equivalents
  opportunities_influenced_amount: "opportunities_influenced",
  opportunities_influenced_in_cycle_amount: "opportunities_influenced_in_cycle",
  opportunities_created_post_engagement_amount:
    "opportunities_created_post_engagement",
  progressed_opportunities_amount: "progressed_opportunities",
  opportunities_influenced_won_amount: "opportunities_influenced_won",
  sourced_opportunities_amount: "sourced_opportunities",
  // Per-entity metrics map to their entity totals
  engagements_per_person: "people_engaged",
  engagements_per_account: "accounts_engaged",
  engagements_per_deal_contact: "deal_contacts_engaged",
};

// Related metrics to fetch for compact card display
const RELATED_METRICS_MAP: Record<string, Array<keyof ChannelMetrics>> = {
  // Amount metrics for opportunity-related metrics
  opportunities_influenced: ["opportunities_influenced_amount"],
  opportunities_influenced_won: ["opportunities_influenced_won_amount"],
  opportunities_influenced_in_cycle: [
    "opportunities_influenced_in_cycle_amount",
  ],
  opportunities_created_post_engagement: [
    "opportunities_created_post_engagement_amount",
  ],
  progressed_opportunities: ["progressed_opportunities_amount"],
  sourced_opportunities: ["sourced_opportunities_amount"],
  // Per-entity metrics for engagement metrics
  accounts_engaged: ["engagements_per_account"],
  people_engaged: ["engagements_per_person"],
  deal_contacts_engaged: ["engagements_per_deal_contact"],
};

// Wrapper component to handle metric mapping
function MetricDrilldownWrapper({
  channelName,
  metric,
  metricValue,
  onClose,
  filters,
}: {
  channelName: string;
  metric: keyof ChannelMetrics;
  metricValue?: number | string;
  onClose: () => void;
  filters: MetricsFilters;
}) {
  // Map derived metrics to their base metrics for drill-down
  const drilldownMetric = METRIC_TO_BASE[metric] || metric;

  // Define metrics to fetch for the compact card
  // Include the clicked metric plus commonly related metrics to ensure compact card displays properly
  const metricsToFetch: (keyof ChannelMetrics)[] = [
    drilldownMetric,
    ...(RELATED_METRICS_MAP[drilldownMetric] || []),
  ];

  // Fetch metrics for the compact card display
  const { data: metrics, loadingStates } = useChannelMetrics({
    channelType: channelName,
    metrics: metricsToFetch,
    statuses: [], // Channel List View doesn't have status selection
    filters,
    enabled: true,
  });

  return (
    <MetricDrilldown
      open={true}
      onOpenChange={(open) => !open && onClose()}
      channelType={channelName}
      metric={drilldownMetric}
      metricValue={metricValue}
      statuses={[]} // Channel List View doesn't have status selection
      filters={filters}
      metrics={metrics}
      loadingStates={loadingStates}
      drillDownLimit={DRILLDOWN_LIMIT}
    />
  );
}
